/** Scoped reads/mutations and owner readback after ambiguous acknowledgements. */
import { useEffect, useInsertionEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useShopifyTrackedMutation } from '../../hooks/useShopifyTrackedMutation'
import { shopifyQueryKey, useShopifyAppContext } from '../../ShopifyAppContext'
import { shopifyApiClient } from '../../shopifyApiClient'
import type { ShopifyTokenSource } from '../../shopifyApiFetch'
import { shopifyQueryPolicies } from '../../queries/shopifyQueryPolicies'
import { integrationVotesSchema } from './integration-page-model'
import { integrationResponse, useIntegrationCatalog } from './useIntegrationCatalog'
export { integrationResponse } from './useIntegrationCatalog'

export function useIntegrationsPage(shopify: ShopifyTokenSource, connected: boolean) {
  const scope = useShopifyAppContext()
  const scopeKey = JSON.stringify([scope.shopDomain, scope.orgId, scope.connectionId])
  const current = useRef(scopeKey)
  useInsertionEffect(() => {
    current.current = scopeKey
  }, [scopeKey])
  const client = useQueryClient()
  const key = shopifyQueryKey(scope.shopDomain, scope.orgId, scope.connectionId, ['testfeed-integrations'])
  const votesKey = shopifyQueryKey(scope.shopDomain, scope.orgId, scope.connectionId, ['integration-votes'])
  const enabled = connected && !!scope.orgId && !!scope.connectionId
  const catalog = useIntegrationCatalog(shopify, connected)
  const capabilities = catalog.data?.capabilities ?? []
  const votes = useQuery({
    queryKey: votesKey,
    enabled: enabled && capabilities.includes('votes'),
    queryFn: async ({ signal }) => {
      const result = integrationVotesSchema.parse(
        await integrationResponse(
          await shopifyApiClient(shopify).api.integrations.votes.$get(undefined, { init: { signal } }),
        ),
      )
      if (result.votes.some((row) => row.organizationId !== scope.orgId))
        throw Error('SHOPIFY_INTEGRATIONS_SCOPE_CHANGED')
      return result
    },
    ...shopifyQueryPolicies.integrations,
  })
  const vote = useShopifyTrackedMutation(shopify, 'vote-provider', {
    onMutate: () => ({ scopeKey }),
    mutationFn: async (input: { provider: string; voted: boolean }) =>
      integrationResponse(await shopifyApiClient(shopify).api.integrations.votes.$post({ json: input })),
    onSettled: async (_data, _error, _variables, mutationScope) => {
      if (current.current !== mutationScope?.scopeKey) return
      await client.invalidateQueries({ queryKey: votesKey })
    },
  })
  useEffect(() => {
    vote.reset()
  }, [scopeKey])
  const refresh = async () => {
    if (current.current !== scopeKey) return
    await Promise.all([
      client.invalidateQueries({ queryKey: key }),
      client.invalidateQueries({ queryKey: votesKey }),
      client.invalidateQueries({
        queryKey: shopifyQueryKey(scope.shopDomain, scope.orgId, scope.connectionId, ['integration-properties']),
      }),
    ])
  }
  return { catalog, capabilities, votes, vote, refresh, scopeKey }
}
